import * as moment from 'moment';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { BreadcrumbsService } from '../../core/breadcrumbs.service';
import { ScrollService } from '../../core/scroll.service';
import { LangService } from '../../core/lang.service';
import { Subscription } from 'rxjs';
import { ChecklistStep, ChecklistStepRoute } from '../pnel-cts-checklist/model/checklist-steps';
import { TEST_SESSION_DUR } from '../../constants/test-session-duration';
import { FormControl, FormGroup } from '@angular/forms';
import { IAccountType } from '../../constants/account-types';
import { LoginGuardService } from '../../api/login-guard.service';
import { MyTestSessionsSetupService, ITimeSlot, SORT_BY_DATETIMESTART, ILocation } from '../my-test-sessions-setup.service';

interface ISession {
  m?:moment.Moment,
  id:number,
  locationId:number,
  sessionNum:number,
  accessCode: {code: string}, 
  hidden: {isActivated: boolean}, 
  dateFromCalEntryDisplay?: string,
  timeFromEntryDisplay?: string,
  dateFromLongLine1?: string,
  dateFromLongLine2?: string,
  dateToLongLine1?: string,
  dateToLongLine2?: string,
}

const DATE_LINE1 = 'dddd h:mma ';
const DATE_LINE2 = 'MMMM D, YYYY';

@Component({
  selector: 'view-cts-restricted',
  templateUrl: './view-cts-restricted.component.html',
  styleUrls: ['./view-cts-restricted.component.scss']
})
export class ViewCtsRestrictedComponent implements OnInit, OnDestroy {

  public breadcrumb = [];

  public setupId:string;
  private routeSub:Subscription;
  private mySetupSub:Subscription;
  
  // load status 
  isLoaded:boolean;
  isLoadFailed:boolean;
  
  sessions:ISession[] = [];
  activeSession;
  isEditingAccessCode:boolean = false;
  accessCodeInput = new FormControl();
  accessCodeFormlet = new FormGroup({
    input: this.accessCodeInput
  })
  locations:Map<number, ILocation> = new Map();
  timeSlots = [];
  isNoTimeSlots:boolean;
  
  constructor(
    public lang:LangService,
    private loginGuard: LoginGuardService, // 
    private breadcrumbsService: BreadcrumbsService,
    private router:Router,
    private route:ActivatedRoute,
    private scrollService:ScrollService,
    private mySetup:MyTestSessionsSetupService,
  ) { }
  
  ngOnInit() {
    this.loginGuard.activate([IAccountType.TEST_ADMIN]);
    this.scrollService.scrollToTop();
    this.routeSub = this.route.params.subscribe(routeParams => {
      this.setupId = routeParams['setupId'];
      this.breadcrumb = this.mySetup.generateBreadcrumbs(this.setupId, 'cts_restricted_title');
    });
    this.initRouteView();
  }

  ngOnDestroy(){

  }

  initRouteView(){
    let isInitialized = false;
    this.mySetupSub = this.mySetup.sub().subscribe(setupInfo => {
      if (setupInfo && !isInitialized){
        isInitialized = true;
        this.initLocations().then( ()=> {
          return this.initLocationTimeSlots();
        })
        .then(()=>{
          if (!this.isNoTimeSlots){
            return this.mySetup.checkoffTimeSlotRestriction(parseInt(this.setupId));
          }
        })
      }
    })
  }

  initLocations(){
    return this.mySetup
      .getLocations(<any>this.setupId)
      .then(locationsData => {
        this.locations = new Map()
        locationsData.forEach((location, locationIndex) => {
          location.__number = locationIndex + 1;
          this.locations.set(location.id, location);
        })
      })
      .catch(e => {
        this.isLoadFailed = true;
      })
  }

  initLocationTimeSlots(){
    this.isLoaded = true;
    const setupId = parseInt(this.setupId);
    this.mySetup
      .getTimeSlots(setupId)
      .then( timeSlots => {
        if (timeSlots.length === 0){
          this.isNoTimeSlots = true;
        }
        timeSlots = timeSlots.sort(SORT_BY_DATETIMESTART);
        this.sessions = timeSlots.map( (timeSlot, i) => {
          const m = moment(timeSlot.dateTimeStart);
          // const mClose = moment(m).add(TEST_SESSION_DUR, 'hours');
          let accessCode = null;
          let hidden = null;
          if (timeSlot.isAccessCodeProtected){
            accessCode = {code: timeSlot.accessCode};
          }
          if (timeSlot.isHidden){
            hidden = {isActivated: true};
          }
          const session:ISession = {
            m,
            id: timeSlot.id,
            locationId: timeSlot.locationId,
            sessionNum: i+1,
            accessCode, 
            hidden, 
            dateFromCalEntryDisplay: m.format('MMM. D'),
            timeFromEntryDisplay: m.format('hh:mma'),
            dateFromLongLine1: m.format(DATE_LINE1),
            dateFromLongLine2: m.format(DATE_LINE2),
            dateToLongLine1: m.format(DATE_LINE1),
            dateToLongLine2: m.format(DATE_LINE2),
          }
          return session;
        })
        this.activeSession = this.sessions[0];
      })
  }

  isActiveSession(session:ISession){
    return this.activeSession === session;
  }
  selectSession(session:ISession){
    this.activeSession = session;
    this.isEditingAccessCode = false;
  }
  getActiveSessionIndex(){
    return this.sessions.indexOf(this.activeSession);
  }
  getActiveSessionNum(){
    const i = this.getActiveSessionIndex();
    return i+1;
  }

  selectPrevSession(){
    const i = this.getActiveSessionIndex();
    if (i > 0){
      this.activeSession = this.sessions[i -1];
    }
  }

  selectNextSession(){
    const i = this.getActiveSessionIndex();
    if (i < this.sessions.length - 1){
      this.activeSession = this.sessions[i+1];
    }
  }

  getDateTimeChecklistRoute(){
    return ['',
      this.lang.c(),
      IAccountType.TEST_ADMIN,
      'create-test-sessions',
      this.setupId,
      ChecklistStepRoute.DateAndTime
    ].join('/')
  }


  toggleNoRestrictions(session:ISession){
    if (!this.isSessNoRestrictions(session)){
      this.updateDbEntry(session, { isAccessCodeProtected:false, isHidden: false }).then(() => {
        session.accessCode = null;
        session.hidden = null;
      });
    }
  }

  toggleAccessCode(session:ISession){
    if (session.accessCode){
      this.updateDbEntry(session, { isAccessCodeProtected:false }).then(() => {
        session.accessCode = null;
      });
    }
    else{
      session.accessCode = {
        code: ''
      }
      this.accessCodeEditStart()
    }
  }
  toggleHidden(session:ISession){
    if (session.hidden){
      this.updateDbEntry(session, { isHidden: false }).then(() => {
        session.hidden = null;
      })
    }
    else{
      this.updateDbEntry(session, { isHidden: true }).then(() => {
        session.hidden = {
          isActivated: true,
        }
      });
    }
  }

  getDbPatch(session:ISession) : Partial<ITimeSlot> {
    const isHidden = !!session.hidden;
    const isAccessCodeProtected = !!session.accessCode;
    let accessCode = null;
    if (isAccessCodeProtected){
      accessCode = session.accessCode.code;
    }
    return {
      isHidden,
      isAccessCodeProtected,
      accessCode
    }
  }

  isUpdating:boolean;
  updateDbEntry(session:ISession, patch: Partial<ITimeSlot>){
    if (this.isUpdating){
      return Promise.reject('is updating')
    }
    this.isUpdating = true;
    return this.mySetup.patchTimeSlotRestriction(session.id, {
      ... this.getDbPatch(session),
      ... patch,
    })
    .then(r => {
      this.isUpdating = false;
    })
    .catch(e=> {
      alert('Could not update record.')
      this.isUpdating = false;
    })
  }

  getSessLocRoom(session:ISession){
    const loc = this.locations.get(session.locationId);
    return loc.room;
  }

  getSessLocCampusBuilding(session:ISession){
    const loc = this.locations.get(session.locationId);
    return loc.campusBuilding;
  }

  getSessLocNum(session:ISession){
    const loc = this.locations.get(session.locationId);
    return loc.__number;
  }

  isSessNoRestrictions(session:ISession){
    return !session.accessCode && !session.hidden;
  }
  isSessAccessCode(session:ISession){
    return session.accessCode;
  }
  isSessHidden(session:ISession){
    return session.hidden;
  }

  accessCodeEditStart(){
    if (this.activeSession.accessCode){
      this.isEditingAccessCode = true;
      this.accessCodeInput.setValue(this.activeSession.accessCode.code);
    }
  }
  accessCodeEditSave(){
    this.isEditingAccessCode = false;
    const session = this.activeSession;
    if (session.accessCode){
      const accessCode = this.accessCodeInput.value;
      this.updateDbEntry(session, { accessCode }).then(() => {
        session.accessCode.code = accessCode;
      });
    }
  }
  accessCodeEditCancel(){
    this.isEditingAccessCode = false;
  }


  getCurrentStepId(){
    return ChecklistStep.RestrictedSessions
  }

  gotoNextRoute(){
    const nextRouteSuffix = ChecklistStepRoute.MakeSessionsAvailable;
    let breadcrumb = this.breadcrumbsService.TESTADMIN_CREATE_TEST_SESSION(this.setupId, nextRouteSuffix);
    this.router.navigate([ breadcrumb.route ]);
  }
}
